import {Card} from "@mui/material";
import {useCallback, useEffect, useMemo, useState} from "react";
import useApiRequest from "../../hooks/useApiRequest";
import SearchBar from "../ui/SearchBar";
import ScopeSearchAutocomplete from "./ScopeSearchAutocomplete";

const normalizeArray = (value) => {
  if (Array.isArray(value?.data)) return value.data;
  if (Array.isArray(value?.items)) return value.items;
  if (Array.isArray(value?.results)) return value.results;
  if (Array.isArray(value)) return value;
  return [];
};

/** Search by the typed term (when present) and map API rows to {id, label}. */
const loadEntity = async (list, searchParam, term, mapOption) => {
  const res = await list(term ? {[searchParam]: term} : undefined);
  return normalizeArray(res?.data).map(mapOption);
};

const ScopeSearchField = ({config, value, onChange, size}) => {
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    if (value === undefined || value === null || value === "") {
      setSelected(null);
    }
  }, [value]);

  return (
    <ScopeSearchAutocomplete
      label={config.label}
      width={config.width ?? 220}
      size={size}
      value={selected}
      onChange={(option) => {
        setSelected(option);
        onChange(option?.id ?? "");
      }}
    />
  );
};

const ScopesResolveFilter = ({onApplyFilters, onClearFilters}) => {
  const {list: listUsers} = useApiRequest({baseUrl: "/users", resource: "users"});
  const {list: listClients} = useApiRequest({baseUrl: "/clients", resource: "clients"});
  const {list: listProducts} = useApiRequest({baseUrl: "/products", resource: "products"});
  const {list: listRoles} = useApiRequest({baseUrl: "/roles", resource: "roles"});
  const {list: listTenants} = useApiRequest({baseUrl: "/tenants", resource: "tenants"});

  const loadUsers = useCallback(
    (term) => loadEntity(listUsers, "email", term, (u) => ({id: u.id, label: u.email || u.id})),
    [listUsers]
  );
  const loadTenants = useCallback(
    (term) => loadEntity(listTenants, "name", term, (t) => ({id: t.id, label: t.name || t.id})),
    [listTenants]
  );
  const loadClients = useCallback(
    (term) => loadEntity(listClients, "name", term, (c) => ({id: c.client_id || c.id, label: c.name || c.id})),
    [listClients]
  );
  const loadProducts = useCallback(
    (term) => loadEntity(listProducts, "name", term, (p) => ({id: p.id, label: p.name || p.id})),
    [listProducts]
  );
  const loadRoles = useCallback(
    (term) =>
      loadEntity(listRoles, "name", term, (r) => ({
        id: r.id,
        label: [r.product_name, r.name].filter(Boolean).join(".")
      })),
    [listRoles]
  );

  const fields = useMemo(
    () => [
      {
        field: "user_id",
        label: "Usuario",
        type: "autocomplete",
        filterMode: "server",
        width: 200,
        loadOptions: loadUsers
      },
      {
        field: "tenant_id",
        label: "Tenant",
        type: "autocomplete",
        filterMode: "server",
        width: 200,
        loadOptions: loadTenants
      },
      {
        field: "client_id",
        label: "Cliente",
        type: "autocomplete",
        filterMode: "server",
        width: 200,
        loadOptions: loadClients
      },
      {
        field: "product_id",
        label: "Producto",
        type: "autocomplete",
        filterMode: "server",
        width: 200,
        loadOptions: loadProducts
      },
      {field: "scope_id", label: "Scope", type: "scopeSearch", width: 220},
      {field: "role_id", label: "Rol", type: "autocomplete", filterMode: "server", width: 200, loadOptions: loadRoles}
    ],
    [loadUsers, loadTenants, loadClients, loadProducts, loadRoles]
  );

  const components = useMemo(() => ({scopeSearch: ScopeSearchField}), []);

  return (
    <Card variant="outlined" sx={{p: 2, backgroundColor: (theme) => theme.palette.background.paper}}>
      <SearchBar fields={fields} onSearch={onApplyFilters} onReset={onClearFilters} components={components} />
    </Card>
  );
};

export default ScopesResolveFilter;
